import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  Download,
  Camera,
  Save,
  Trash2,
  Eye,
  Play,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import BulkOperationResultPanel from './BulkOperationResultPanel';
import type {
  BulkOperationResultDto,
  BulkOperationItemDto,
} from '../../types/cutover';
import './ProcedureSetupTab.css';

type OverrideAction = 'Apply' | 'Skip';

export default function ProcedureSetupTab() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [previewResult, setPreviewResult] = useState<BulkOperationResultDto | null>(null);
  const [applyResult, setApplyResult] = useState<BulkOperationResultDto | null>(null);
  const [overrides, setOverrides] = useState<Record<string, OverrideAction>>({});
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Queries
  const {
    data: templates = [],
    isLoading: templatesLoading,
  } = useQuery({
    queryKey: ['cutover', 'templates'],
    queryFn: cutoverApi.getTemplates,
  });

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;

  // Mutations
  const createFromSnapshotMutation = useMutation({
    mutationFn: async () => {
      const snapshot = await cutoverApi.getProcedureSnapshot();
      return cutoverApi.createTemplate({
        name: `Snapshot ${new Date().toLocaleString()}`,
        description: 'Created from current live procedure state',
        templateJson: JSON.stringify(snapshot),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['cutover', 'templates'] });
      setSelectedTemplateId(result.id);
      setPreviewResult(null);
      setApplyResult(null);
    },
  });

  const importMutation = useMutation({
    mutationFn: (request: { name: string; description: string; templateJson: string }) =>
      cutoverApi.importTemplate(request),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['cutover', 'templates'] });
      setSelectedTemplateId(result.id);
      setPreviewResult(null);
      setApplyResult(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('No template selected');
      return cutoverApi.updateTemplate(selectedTemplateId, {
        name: editName,
        description: editDescription,
        templateJson: selectedTemplate?.templateJson ?? '{}',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cutover', 'templates'] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('No template selected');
      return cutoverApi.deleteTemplate(selectedTemplateId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cutover', 'templates'] });
      setSelectedTemplateId(null);
      setPreviewResult(null);
      setApplyResult(null);
      setIsEditing(false);
      setShowDeleteConfirm(false);
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('No template selected');
      return cutoverApi.previewTemplate(selectedTemplateId);
    },
    onSuccess: (result) => {
      setPreviewResult(result);
      setApplyResult(null);
      // Initialize overrides — Conflict items default to Skip
      const initial: Record<string, OverrideAction> = {};
      result.items.forEach((item) => {
        if (item.action === 'Conflict') {
          initial[itemKey(item)] = 'Skip';
        }
      });
      setOverrides(initial);
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('No template selected');
      return cutoverApi.applyTemplate(selectedTemplateId);
    },
    onSuccess: (result) => {
      setApplyResult(result);
      setShowApplyConfirm(false);
      setPreviewResult(null);
    },
  });

  // Helpers
  function itemKey(item: BulkOperationItemDto) {
    return `${item.itemType}::${item.itemName}`;
  }

  function handleTemplateSelect(id: number | null) {
    setSelectedTemplateId(id);
    setPreviewResult(null);
    setApplyResult(null);
    setIsEditing(false);
    setOverrides({});
    if (id) {
      const tmpl = templates.find((t) => t.id === id);
      if (tmpl) {
        setEditName(tmpl.name);
        setEditDescription(tmpl.description ?? '');
      }
    }
  }

  function handleStartEdit() {
    if (!selectedTemplate) return;
    setEditName(selectedTemplate.name);
    setEditDescription(selectedTemplate.description ?? '');
    setIsEditing(true);
  }

  function handleExport() {
    if (!selectedTemplateId) return;
    cutoverApi.exportTemplate(selectedTemplateId).then((tmpl) => {
      const blob = new Blob([JSON.stringify(tmpl, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `procedure-template-${tmpl.name.replace(/\s+/g, '-').toLowerCase()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    });
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        importMutation.mutate({
          name: parsed.name ?? file.name.replace('.json', ''),
          description: parsed.description ?? 'Imported template',
          templateJson: typeof parsed.templateJson === 'string'
            ? parsed.templateJson
            : JSON.stringify(parsed.templateJson ?? parsed),
        });
      } catch {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  }

  function handleOverrideChange(item: BulkOperationItemDto, action: OverrideAction) {
    setOverrides((prev) => ({ ...prev, [itemKey(item)]: action }));
  }

  // Computed
  const conflictCount = previewResult?.items.filter((i) => i.action === 'Conflict').length ?? 0;
  const createCount = previewResult?.items.filter((i) => i.action === 'Created').length ?? 0;
  const updateCount = previewResult?.items.filter((i) => i.action === 'Updated').length ?? 0;
  const isAnyLoading =
    createFromSnapshotMutation.isPending ||
    importMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    previewMutation.isPending ||
    applyMutation.isPending;

  return (
    <div className="pst">
      {/* Template Selector Row */}
      <div className="pst-selector-row">
        <div className="pst-selector">
          <label htmlFor="template-select" className="pst-label">Template</label>
          <select
            id="template-select"
            className="pst-select"
            value={selectedTemplateId ?? ''}
            onChange={(e) => handleTemplateSelect(e.target.value ? Number(e.target.value) : null)}
            disabled={templatesLoading}
          >
            <option value="">— Select a template —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (v{t.version})
              </option>
            ))}
          </select>
        </div>
        <div className="pst-selector-actions">
          <button
            type="button"
            className="pst-btn pst-btn-secondary"
            onClick={() => createFromSnapshotMutation.mutate()}
            disabled={isAnyLoading}
            title="Capture current live procedures as a new template"
          >
            {createFromSnapshotMutation.isPending ? <Loader2 size={14} className="pst-spin" /> : <Camera size={14} />}
            Create from Current
          </button>
          <button
            type="button"
            className="pst-btn pst-btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isAnyLoading}
            title="Import a JSON template file"
          >
            <Upload size={14} />
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="pst-hidden-file-input"
            aria-hidden="true"
            tabIndex={-1}
            onChange={handleImportFile}
          />
          <button
            type="button"
            className="pst-btn pst-btn-secondary"
            onClick={handleExport}
            disabled={!selectedTemplateId || isAnyLoading}
            title="Export selected template as JSON"
          >
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Error Messages */}
      {(createFromSnapshotMutation.error || importMutation.error || previewMutation.error || applyMutation.error) && (
        <div className="pst-error">
          <AlertCircle size={16} />
          <span>
            {String(
              (createFromSnapshotMutation.error || importMutation.error || previewMutation.error || applyMutation.error) as Error
            )}
          </span>
        </div>
      )}

      {/* Template Details */}
      {selectedTemplate && (
        <div className="pst-details">
          <div className="pst-details-header">
            <h3 className="pst-details-title">Template Details</h3>
            <div className="pst-details-actions">
              {!isEditing ? (
                <>
                  <button
                    type="button"
                    className="pst-btn pst-btn-secondary pst-btn-sm"
                    onClick={handleStartEdit}
                    disabled={isAnyLoading}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="pst-btn pst-btn-danger pst-btn-sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isAnyLoading}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="pst-btn pst-btn-primary pst-btn-sm"
                    onClick={() => updateMutation.mutate()}
                    disabled={!editName.trim() || isAnyLoading}
                  >
                    {updateMutation.isPending ? <Loader2 size={14} className="pst-spin" /> : <Save size={14} />}
                    Save
                  </button>
                  <button
                    type="button"
                    className="pst-btn pst-btn-secondary pst-btn-sm"
                    onClick={() => setIsEditing(false)}
                    disabled={isAnyLoading}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="pst-details-body">
            {isEditing ? (
              <div className="pst-edit-form">
                <div className="pst-field">
                  <label htmlFor="edit-name">Name</label>
                  <input
                    id="edit-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="pst-input"
                  />
                </div>
                <div className="pst-field">
                  <label htmlFor="edit-desc">Description</label>
                  <input
                    id="edit-desc"
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="pst-input"
                  />
                </div>
              </div>
            ) : (
              <div className="pst-detail-grid">
                <div className="pst-detail-item">
                  <span className="pst-detail-label">Name</span>
                  <span className="pst-detail-value">{selectedTemplate.name}</span>
                </div>
                <div className="pst-detail-item">
                  <span className="pst-detail-label">Description</span>
                  <span className="pst-detail-value">{selectedTemplate.description || '—'}</span>
                </div>
                <div className="pst-detail-item">
                  <span className="pst-detail-label">Version</span>
                  <span className="pst-detail-value">{selectedTemplate.version}</span>
                </div>
                <div className="pst-detail-item">
                  <span className="pst-detail-label">Created</span>
                  <span className="pst-detail-value">
                    {new Date(selectedTemplate.createdAt).toLocaleString()} by {selectedTemplate.createdBy}
                  </span>
                </div>
                {selectedTemplate.modifiedAt && (
                  <div className="pst-detail-item">
                    <span className="pst-detail-label">Modified</span>
                    <span className="pst-detail-value">
                      {new Date(selectedTemplate.modifiedAt).toLocaleString()} by {selectedTemplate.modifiedBy}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview / Apply Actions */}
      {selectedTemplate && !applyResult && (
        <div className="pst-action-bar">
          <button
            type="button"
            className="pst-btn pst-btn-secondary"
            onClick={() => previewMutation.mutate()}
            disabled={isAnyLoading}
          >
            {previewMutation.isPending ? <Loader2 size={14} className="pst-spin" /> : <Eye size={14} />}
            Preview Changes
          </button>
          {previewResult && (
            <button
              type="button"
              className="pst-btn pst-btn-primary"
              onClick={() => setShowApplyConfirm(true)}
              disabled={isAnyLoading}
            >
              {applyMutation.isPending ? <Loader2 size={14} className="pst-spin" /> : <Play size={14} />}
              Apply Template
            </button>
          )}
        </div>
      )}

      {/* Preview Results Table */}
      {previewResult && !applyResult && (
        <div className="pst-preview">
          <div className="pst-preview-header">
            <h3>Preview — Dry Run Results</h3>
            <div className="pst-preview-summary">
              <span className="pst-badge pst-badge-create">{createCount} create</span>
              <span className="pst-badge pst-badge-update">{updateCount} update</span>
              {conflictCount > 0 && (
                <span className="pst-badge pst-badge-conflict">{conflictCount} conflicts</span>
              )}
              <span className="pst-badge pst-badge-skip">{previewResult.skippedCount} skip</span>
            </div>
          </div>
          <div className="pst-preview-table-wrapper">
            <table className="pst-preview-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Current State</th>
                  <th>Proposed Action</th>
                  <th>Group</th>
                  <th>Override</th>
                </tr>
              </thead>
              <tbody>
                {previewResult.items.map((item, idx) => (
                  <tr key={`${item.itemType}-${item.itemName}-${idx}`} className={`pst-row-${item.action.toLowerCase()}`}>
                    <td className="pst-cell-type">{item.itemType}</td>
                    <td className="pst-cell-name">{item.itemName}</td>
                    <td className="pst-cell-state">{item.details ?? '—'}</td>
                    <td>
                      <span className={`pst-action-badge pst-action-${item.action.toLowerCase()}`}>
                        {item.action}
                      </span>
                    </td>
                    <td className="pst-cell-group">{(item as unknown as { groupName?: string }).groupName ?? '—'}</td>
                    <td className="pst-cell-override">
                      {item.action === 'Conflict' ? (
                        <select
                          className="pst-override-select"
                          value={overrides[itemKey(item)] ?? 'Skip'}
                          onChange={(e) => handleOverrideChange(item, e.target.value as OverrideAction)}
                          aria-label={`Override action for ${item.itemName}`}
                        >
                          <option value="Apply">Apply</option>
                          <option value="Skip">Skip</option>
                        </select>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Apply Results */}
      {applyResult && (
        <div className="pst-results">
          <BulkOperationResultPanel result={applyResult} title="Template Applied" />
          <div className="pst-results-actions">
            <button
              type="button"
              className="pst-btn pst-btn-secondary"
              onClick={() => {
                setApplyResult(null);
                setPreviewResult(null);
              }}
            >
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedTemplate && !templatesLoading && templates.length === 0 && (
        <div className="pst-empty">
          <p>No templates found. Create one from the current procedure state or import a JSON file.</p>
        </div>
      )}

      {/* Apply Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApplyConfirm}
        title="Apply Template"
        message={`Apply template '${selectedTemplate?.name}' to the current environment? This will create/update ${createCount + updateCount} procedures and groups. This action cannot be easily undone.`}
        confirmText="Apply"
        cancelText="Cancel"
        onConfirm={() => applyMutation.mutate()}
        onCancel={() => setShowApplyConfirm(false)}
        danger
        loading={applyMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Template"
        message={`Delete template '${selectedTemplate?.name}'? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteConfirm(false)}
        danger
        loading={deleteMutation.isPending}
      />
    </div>
  );
}